<script setup>
  import {computed, inject, onBeforeMount, onBeforeUnmount, ref} from "vue";

  import ComponentAutocomplete from "@components/Autocomplete/Autocomplete.vue";
  import {usePage} from "@inertiajs/vue3";
  import {circumstance} from "@pages/Records/Components/Sections/MedicationBar/Components/Indications/enum.js";
  import {coreRouter} from "@utils/coreRouter/coreRouter.js";
  import {getAge} from "@utils/Helpers/AgeCalulator.js";

  defineOptions({
    name: "RecordComponentMinCSearch",
  });

  const props = defineProps({
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    rawValue: {
      type: Object,
      default: () => {
        return {};
      },
    },
    resetOnSelect: {
      type: Boolean,
      default: false,
    },
    filter: {
      type: String,
      default: null,
    },
    customStyle: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    filterList: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["input", "cleared"]);

  const page = usePage();

  const privacy = inject("$privacy");

  const autocomplete = ref(null);

  const isDirty = ref(false);
  const item = ref(null);
  const message = ref(null);
  const paginationLinks = ref(null);
  const rawCircumstances = ref([]);
  const patientAge = ref(null);

  const validation = computed(() => {
    if (isDirty.value || message.value) {
      return "Unbekannte Eingabe. Bitte eine Auswahl treffen.";
    } else {
      return "";
    }
  });

  const helperText = computed(() => {
    if (props.rawValue && !props.rawValue.abdata_key_miv && props.rawValue.abdata_key_icdc) {
      return "Diesem ICD-Code kann kein MediCheck-Code zugeordnet werden (Checks funktionieren tw. nicht). Wenn möglich, bitte anderen wählen.";
    }

    return "";
  });

  const recommendedCircumstances = computed(() => {
    return rawCircumstances.value.filter((mapCircumstance) => {
      // hide when already added
      if (Object.values(page.props.circumstances ?? {}).includes(mapCircumstance.Key_MIV)) {
        return false;
      }

      if (patientAge.value >= 65 && patientAge.value < 75 && mapCircumstance.Key_MIV === circumstance.OLDER_PATIENT) {
        return true;
      } else if (patientAge.value >= 75 && mapCircumstance.Key_MIV === circumstance.GERIATRIC_PATIENT) {
        return true;
      } else if ([circumstance.OLDER_PATIENT, circumstance.GERIATRIC_PATIENT].includes(mapCircumstance.Key_MIV)) {
        return false;
      }

      // male => hide all female specific circumstances
      if (
        page.props.patient.gender === "male" &&
        [
          circumstance.CLIMACTERIC,
          circumstance.POST_CLIMACTERIC,
          circumstance.CHILDBEARING_AGED_WOMAN,
          circumstance.PREGNANT_WOMAN,
          circumstance.LACTATING_WOMAN,
        ].includes(mapCircumstance.Key_MIV)
      ) {
        return false;
      }
      // female
      else if (page.props.patient.gender === "female") {
        // => hide all male specific circumstances
        if ([circumstance.PROCREATIVE_MAN].includes(mapCircumstance.Key_MIV)) {
          return false;
        }

        // when age is <= 45, hide climacteric and post climacteric
        if (
          patientAge.value <= 45 &&
          [circumstance.CLIMACTERIC, circumstance.POST_CLIMACTERIC].includes(mapCircumstance.Key_MIV)
        ) {
          return false;
        }
        // when age is > 45, hide childbearing aged woman, pregnant woman and lactating woman
        else if (
          patientAge.value > 45 &&
          [circumstance.CHILDBEARING_AGED_WOMAN, circumstance.PREGNANT_WOMAN, circumstance.LACTATING_WOMAN].includes(
            mapCircumstance.Key_MIV,
          )
        ) {
          return false;
        }
      }

      if (page.props.circumstances === undefined) {
        return true;
      }

      // climacteric selected => hide post climacteric, childbearing aged woman, pregnant woman and lactating woman
      if (
        Object.values(page.props.circumstances).includes(circumstance.CLIMACTERIC) &&
        [
          circumstance.POST_CLIMACTERIC,
          circumstance.CHILDBEARING_AGED_WOMAN,
          circumstance.PREGNANT_WOMAN,
          circumstance.LACTATING_WOMAN,
        ].includes(mapCircumstance.Key_MIV)
      ) {
        return false;
      }

      // post climacteric selected => hide climacteric, childbearing aged woman, pregnant woman and lactating woman
      if (
        Object.values(page.props.circumstances).includes(circumstance.POST_CLIMACTERIC) &&
        [
          circumstance.CLIMACTERIC,
          circumstance.CHILDBEARING_AGED_WOMAN,
          circumstance.PREGNANT_WOMAN,
          circumstance.LACTATING_WOMAN,
        ].includes(mapCircumstance.Key_MIV)
      ) {
        return false;
      }

      // childbearing aged woman selected => hide climacteric and post climacteric
      if (
        Object.values(page.props.circumstances).includes(circumstance.CHILDBEARING_AGED_WOMAN) &&
        [circumstance.CLIMACTERIC, circumstance.POST_CLIMACTERIC].includes(mapCircumstance.Key_MIV)
      ) {
        return false;
      }

      // pregnant woman selected => hide climacteric and post climacteric
      if (
        Object.values(page.props.circumstances).includes(circumstance.PREGNANT_WOMAN) &&
        [circumstance.CLIMACTERIC, circumstance.POST_CLIMACTERIC].includes(mapCircumstance.Key_MIV)
      ) {
        return false;
      }

      return true;
    });
  });

  onBeforeMount(() => {
    privacy.decryptValue(usePage().props.patient.birthdate).then((encryptedValue) => {
      patientAge.value = getAge(encryptedValue);
    });

    item.value = props.value;

    if (props.rawValue && props.rawValue.name_icdc) {
      if (!item.value) {
        item.value = {
          Name: props.rawValue.abdata_key_icdc,
        };
      }

      item.value = item.value.Name + " (" + props.rawValue.name_icdc + ") ";
    }

    // we have to preload the circumstances and decide,
    // which of them we have to provide as autocomplete
    // recommendations depending on gender, age and
    // already selected circumstances

    if (props.value === null || (props.value === "" && props.filter === "diseases")) {
      coreRouter.loadMinCData(
        {filter: "diseases", "q[key_miv]": Object.values(circumstance)},
        {
          onSuccess: (response) => {
            rawCircumstances.value = response.data;
          },
        },
      );
    }
  });

  onBeforeUnmount(() => {
    reset();
  });

  const search = (searchTerm) => {
    let searchQuery = "?page=1";

    if (/^[A-Za-z]{1}[0-9]{2}[.\-0-9A-Z]{0,3}$/.test(searchTerm)) {
      searchQuery += "&q[key_icdc]=" + searchTerm;
    } else {
      searchQuery += "&q[name]=" + searchTerm;
    }

    if (page.props.patient.gender) {
      searchQuery += "&q[gender]=" + page.props.patient.gender;
    }

    if (props.filter) {
      searchQuery += "&filter=" + props.filter;
    }

    return new Promise((resolve) => {
      coreRouter.searchIndicationByName(searchQuery, {
        onSuccess: (response) => {
          if (props.filterList) {
            filterOptionList(response.data).then((filteredList) => {
              resolve(filteredList);
            });
          } else {
            resolve(response.data);
          }
        },
        onError: () => {
          resolve(false);
        },
      });
    });
  };

  const filterOptionList = (list) => {
    return new Promise((resolve) => {
      const filteredList = list.filter((item) => {
        const exists = page.props[props.filter].data.some((recordItem) => recordItem.abdata_key_miv === item.Key_MIV);

        return !exists;
      });

      resolve(filteredList);
    });
  };

  const loadNextPage = (next) => {
    return new Promise((resolve) => {
      coreRouter.searchIndicationByName(decodeURI(next), {
        onSuccess: (response) => {
          if (!response || !response.data) return false;

          paginationLinks.value = response.links || {};
          resolve(response.data);
        },
        onError: () => {
          resolve(false);
        },
      });
    });
  };

  const select = (selectedItem) => {
    item.value = selectedItem;
    isDirty.value = false;

    if (
      !props.resetOnSelect &&
      selectedItem &&
      item.value.indication_leads_to_disease &&
      item.value.indication_leads_to_disease.length > 0
    ) {
      item.value.Name += " (" + item.value.indication_leads_to_disease[0].Name + ")";
    }

    if (props.resetOnSelect) {
      reset();
    }

    emit("input", item.value);
  };

  const reset = () => {
    isDirty.value = false;
    autocomplete.value?.reset();
  };

  const close = () => {
    globalThis.setTimeout(() => {
      if (
        item.value !== null &&
        item.value.Name &&
        item.value.Name.length > 0 &&
        item.value.Name !== props.value && // key miv name
        item.value.Name !== props.rawValue.abdata_key_icdc + " (" + props.rawValue.name_icdc + ")" // key miv name (icdc name)
      ) {
        isDirty.value = true;
      } else if (item.value === null && props.value && props.value !== "") {
        item.value = {
          Name: props.value,
        };
      }
    }, 200);
  };

  const handleClearedField = (clearedItem) => {
    emit("cleared", clearedItem);
  };

  const focus = () => {
    if (autocomplete.value) {
      autocomplete.value.focus();
    }
  };

  defineExpose({
    focus,
  });
</script>

<template>
  <ComponentAutocomplete
    ref="autocomplete"
    v-model="item"
    :label="label"
    :fetch-method="search"
    :fetch-pagination-method="loadNextPage"
    :pagination-links="paginationLinks"
    :clearable="clearable"
    :key-name="'Name'"
    :validation="validation"
    :helper-text="helperText"
    :placeholder="placeholder"
    :disabled="disabled"
    :test-id="getTestId('min-c-search')"
    @cleared="handleClearedField"
    @closed="close"
    @selected="select"
  >
    <template #autocomplete-item="itemProps">
      <div class="py-1">
        <div class="leading-tight">
          <span>{{ itemProps.item.Name }}</span>

          <span v-if="itemProps.item.icdcs && itemProps.item.icdcs.length > 0" class="text-gray-600">
            <span v-if="!itemProps.item.Name">{{ itemProps.item.icdcs[0].Key_ICDC }}</span>
            ({{ itemProps.item.icdcs[0].Name }})
          </span>
        </div>

        <div class="text-sm leading-tight">
          <em v-if="itemProps.item.Key_MIV && itemProps.item.icdcs && itemProps.item.icdcs.length > 0">
            Hinweis: Die eingegebene ICD-10-codierte Erkrankung ist mit einer im MediCheck (gem. ABDATA) festgelegten
            (ähnlichen) Erkrankung verknüpft und wird durch diese ersetzt.
          </em>
          <em v-if="!itemProps.item.Key_MIV">
            Hinweis: Diesem ICD-Code kann kein MediCheck-Code zugeordnet werden (Checks funktionieren tw. nicht). Wenn
            möglich, bitte anderen wählen.
          </em>
          <em v-if="itemProps.item.Geschlecht === 1">männlicher Patient</em>
          <em v-else-if="itemProps.item.Geschlecht === 2">weibliche Patientin</em>
          <em v-else-if="itemProps.item.Geschlecht === 4">divers geschlechtliche(r) Patient(in)</em>
        </div>
      </div>
    </template>

    <template v-if="filter === 'diseases'" #autocomplete-recommendation="">
      <div v-if="recommendedCircumstances.length > 0" class="p-3">
        <span class="text-gray-500">Ggf. passende Lebensumstände:</span>
        <ul class="flex flex-wrap">
          <li
            v-for="(recommendation, index) in recommendedCircumstances"
            :key="index"
            class="last:after:content-[] mr-2 after:content-[',']"
          >
            <button
              class="cursor-pointer text-mcred hover:underline"
              :data-test="getTestId(`min-c-search-recommend-${index}`)"
              @click.prevent="select(recommendation)"
            >
              {{ recommendation.Name }}
            </button>
          </li>
        </ul>
      </div>
    </template>

    <!-- TODO BUG? -->
    <template #autocomplete-empty="emptyProps">
      <div v-if="emptyProps.term.length > 0" class="flex h-12 w-96 items-stretch">
        <div class="w-full self-center text-center">Keine Einträge gefunden!</div>
      </div>
      <div v-else class="flex h-12 w-96 items-stretch">
        <div class="w-full self-center text-center">Bitte einen Suchbegriff eingeben</div>
      </div>
    </template>
  </ComponentAutocomplete>
</template>
